'use client';

import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';

interface GalleryImage {
  id: string;
  src: string;
  alt: string;
  caption: string;
}

const DEFAULT_GALLERY: GalleryImage[] = [
  { id: 'gal-1', src: '/assets/images/IMG-20250501-WA0005-1786297878800.jpg', alt: 'YASH Computers Institute - Main Campus', caption: 'YASH Computers — Main Campus' },
  { id: 'gal-2', src: '/assets/images/IMG-20250501-WA0005-1786297878800.jpg', alt: 'Computer Lab at YASH Computers', caption: 'State-of-the-art Computer Lab' },
  { id: 'gal-3', src: '/assets/images/IMG-20250501-WA0005-1786297878800.jpg', alt: 'Students attending class at YASH Computers', caption: 'Interactive Classroom Sessions' },
  { id: 'gal-4', src: '/assets/images/IMG-20250501-WA0005-1786297878800.jpg', alt: 'Certificate distribution ceremony at YASH Computers', caption: 'Certificate Distribution Ceremony' },
];

interface ImageGalleryProps {
  images?: GalleryImage[];
  isAdmin?: boolean;
  onAddImage?: (img: GalleryImage) => void;
}

export default function ImageGallery({ images = DEFAULT_GALLERY, isAdmin = false, onAddImage }: ImageGalleryProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>(images);

  const openLightbox = (idx: number) => setLightboxIndex(idx);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = () => setLightboxIndex(i => (i !== null ? (i - 1 + galleryImages.length) % galleryImages.length : 0));
  const nextImage = () => setLightboxIndex(i => (i !== null ? (i + 1) % galleryImages.length : 0));

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const newImg: GalleryImage = {
      id: `gal-${Date.now()}`,
      src: url,
      alt: file.name,
      caption: file.name.replace(/\.[^/.]+$/, ''),
    };
    setGalleryImages(prev => [...prev, newImg]);
    if (onAddImage) onAddImage(newImg);
  };

  return (
    <section className="py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gradient-primary">Photo Gallery</h2>
            <p className="text-muted-foreground text-sm mt-1">Life at YASH Computers</p>
          </div>
          {isAdmin && (
            <label className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold btn-primary cursor-pointer">
              <ImageIcon size={15} />
              Add Photo
              <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
            </label>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {galleryImages.map((img, idx) => (
            <div
              key={img.id}
              className="relative group cursor-pointer rounded-xl overflow-hidden aspect-square"
              style={{ border: '1px solid var(--border)' }}
              onClick={() => openLightbox(idx)}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                <p className="text-white text-xs font-medium leading-tight">{img.caption}</p>
              </div>
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-primary/50 rounded-xl transition-all duration-300" />
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.92)' }}
          onClick={closeLightbox}
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors text-white"
            onClick={closeLightbox}
          >
            <X size={24} />
          </button>
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full hover:bg-white/10 transition-colors text-white"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
          >
            <ChevronLeft size={28} />
          </button>
          <div className="max-w-4xl max-h-[85vh] flex flex-col items-center gap-3" onClick={e => e.stopPropagation()}>
            <img
              src={galleryImages[lightboxIndex].src}
              alt={galleryImages[lightboxIndex].alt}
              className="max-h-[75vh] max-w-full object-contain rounded-xl"
            />
            <p className="text-white/80 text-sm">{galleryImages[lightboxIndex].caption}</p>
            <p className="text-white/40 text-xs">{lightboxIndex + 1} / {galleryImages.length}</p>
          </div>
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full hover:bg-white/10 transition-colors text-white"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
          >
            <ChevronRight size={28} />
          </button>
        </div>
      )}
    </section>
  );
}
